import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Tab, TabPane } from 'semantic-ui-react'
import DairyQuery from '~/QueryHooks/DairyQuery'
import Layout from '~/components/Layout'
import TableComponent from '~/components/TableComponent'
import i18n from '~/components/i18n'
import { El as LayoutEl } from '~/components/Layout/style'
import CreateEditMilkPrice from './CreateEditMilkPrice'
import { MilkSegmentationType } from '~/api/services/dairy'
import { Nullable } from '~/types'

const getTableColumns = () => [
  { name: i18n.t('Month'), key: 'month', type: 'dateRibbon', format: 'DD.MM.YYYY' },
  { name: i18n.t('Base Price'), key: 'basePrice' },
  { name: i18n.t('A-Price (Rp./kg)'), key: 'aPrice' },
  { name: i18n.t('B-Price (Rp./kg)'), key: 'bPrice' },
  { name: i18n.t('Planning Bonus'), key: 'planningBonus' },
  { name: i18n.t('UL Deduction'), key: 'ulDeduction' },
  { name: i18n.t('Selection Bonus'), key: 'selectionBonus' },
  { name: i18n.t('Milk Price Fixed'), key: 'priceSet', type: 'verified' },
  { name: i18n.t('A Segmentation'), key: 'aSegmentation' },
  { name: i18n.t('B Segmentation'), key: 'bSegmentation' },
  { name: i18n.t('Segmentation Fixed'), key: 'segmentationFixed', type: 'verified' },
  { name: i18n.t('Actions') }
]

const actions = [
  {
    popupMessage: i18n.t('Edit'),
    type: 'icon',
    name: 'pencil',
    size: 'large',
    actionType: 'edit',
    displayedText: i18n.t('No actions')
  }
]

export type DrawerMilkPriceType = { isVisible: boolean; milkTypeId: number | null; idMilkPrice: Nullable<string | number> }

const CurrentMilkPrice: React.FC = () => {
  const { t } = useTranslation()

  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 20,
    milkTypeId: ''
  });
  const [activeIndex, setActiveIndex] = useState(0)
  const [drawerData, setDrawerData] = useState<DrawerMilkPriceType>({
    isVisible: false,
    milkTypeId: null,
    idMilkPrice: null
  })

  const {
    data: options,
    isLoading: isOptionsLoading,
    refetch: refetchOptions
  } = DairyQuery.getSegmentationPricesOptions()

  const {
    data: prices,
    isLoading: isPricesLoading,
    isFetching: isFetchingData,
    refetch: fetchPrices
  } = DairyQuery.getSegmentationPrices({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  })

  useEffect(() => {
    if (options?.milkTypes?.length && !dataFiltered.milkTypeId) {
      setDataFiltered(prev => ({ ...prev, milkTypeId: options?.milkTypes[activeIndex]?.id?.toString() }))
    }
  }, [options])

  useEffect(() => {
    fetchPrices()
    refetchOptions()
  }, [t])

  function handleActionClick(row, type, milkTypeId: number) {
    switch (type) {
      case 'edit':
        return setDrawerData({ isVisible: true, milkTypeId, idMilkPrice: row?.id })
      default:
        return
    }
  }

  function renderActions(milkType) {
    return <LayoutEl.PageHeaderActionButton
      onClick={() => setDrawerData({ isVisible: true, milkTypeId: milkType.id, idMilkPrice: null })}
      style={{ marginBottom: '1rem' }}
    >
      {t('Create milk price')}
    </LayoutEl.PageHeaderActionButton>
  }

  function renderTabPane(milkType: MilkSegmentationType) {

    return {
      menuItem: milkType.name,
      render: () => (
        <TabPane style={{ border: 'none', padding: '1rem 0', background: 'none' }}>
          <div style={{ display: 'flex', justifyContent: 'end' }}>
            {renderActions(milkType)}
          </div>
          <TableComponent
            isFetchingData={isFetchingData}
            animated
            actions={actions}
            tableColumnHeaders={getTableColumns()}
            handleClickAction={(row, type) => handleActionClick(row, type, milkType.id)}
            dataFiltered={dataFiltered}
            handleChangeDataFiltered={(data) => setDataFiltered(data)}
            rows={rows}
            total={filtered}
          />
        </TabPane>
      )
    }
  }

  const { filtered, rows } = prices ? prices : { filtered: 0, rows: [] };

  return <Layout
    title={''}
    isBusy={isOptionsLoading || isPricesLoading}
    childContainerSize={'auto'}
    fontSizeHeader={'18px'}
    useSpinner
  >
    <Tab
      menu={{ secondary: true, pointing: true }}
      activeIndex={activeIndex}
      onTabChange={(_, { activeIndex }) => {
        setActiveIndex(activeIndex as number)
        setDataFiltered({
          ...dataFiltered,
          milkTypeId: options?.milkTypes[activeIndex]?.id as string
        })
      }}
      panes={options?.milkTypes?.map(milkType => renderTabPane(milkType)) || []}
    />
    <CreateEditMilkPrice
      drawerData={drawerData}
      months={options?.months || []}
      fetchData={() => fetchPrices()}
      handleHiddenDrawer={() => setDrawerData({ isVisible: false, milkTypeId: null, idMilkPrice: null })}
    />
  </Layout>
}

export default CurrentMilkPrice
